import { useRef, useState } from "react";
import { Card, Flex, Row, Col, Skeleton, InputRef } from "antd";
import { WalletOutlined, CalendarOutlined, BarChartOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { fetchAnalyticsRows, getSalesServicesAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency } from "shared/utils";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { getYtdDayRange } from "shared/utils/dayjs";
import { AnalyticsRange, CustomDayRange, SalesServicesAnalytics, SalesServicesRow } from "shared/types";
import PATHS from "shared/constants/paths";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import SessionDetailModal from "./SessionDetailModal.component";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { servicesSpec } from "pdf/reports/specs/archetype2";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// "Pendapatan dari Booking" — earned/consumption-basis: value recognized as
// classes actually get delivered, via the same calcConsumedRevenue formula
// used everywhere else in the app. An unlimited-plan booking contributes 0
// here on purpose — that cash was already counted under "Penjualan Paket" at
// the moment the membership was sold, not at the moment it's used.
const SalesServicesPage = () => {
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const { data, isLoading } = getSalesServicesAnalytics(range, customRange);
  const [selectedSession, setSelectedSession] = useState<SalesServicesRow | null>(null);
  const classSearchInput = useRef<InputRef>(null);
  const instructorSearchInput = useRef<InputRef>(null);

  const columns: ReportColumn<SalesServicesRow>[] = [
    reportColumn<SalesServicesRow>("class", {
      dataIndex: "class_name",
      search: getColumnSearchProps<SalesServicesRow, "class_name">({
        config: "class_name",
        searchInput: classSearchInput,
      }),
    }),
    reportColumn<SalesServicesRow>("start_time", {
      dataIndex: "start_time",
      sorter: (a, b) => a.start_time.localeCompare(b.start_time),
      defaultSortOrder: "descend",
    }),
    reportColumn<SalesServicesRow>("instructor_text", {
      dataIndex: "instructor_name",
      search: getColumnSearchProps<SalesServicesRow, "instructor_name">({
        config: "instructor_name",
        searchInput: instructorSearchInput,
      }),
    }),
    reportColumn<SalesServicesRow>("attendees", {
      dataIndex: "attendees",
      sorter: (a, b) => a.attendees - b.attendees,
    }),
    reportColumn<SalesServicesRow>("booking_revenue", {
      dataIndex: "revenue",
      sorter: (a, b) => a.revenue - b.revenue,
    }),
  ];

  // One source for both exports: the Excel query string and the PDF's
  // larger-row refetch must describe the same window.
  const windowParams = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };

  // The download fetches the full row set when the table holds only the
  // display-capped rows; the picker plans on that same set.
  const pageCount = useReportPageCount(servicesSpec, data, sortState, {
    needsAll: !!data && data.sessions_completed.value > data.table.length,
    loadAll: () => fetchAnalyticsRows<SalesServicesAnalytics>("sales", { ...windowParams, view: "services" }, MAX_PDF_ROWS),
  });

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.sales.report_services")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="pendapatan-booking"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.sessions_completed.value, MAX_PDF_ROWS),
                  total: data.sessions_completed.value,
                },
                fileName: `spott-${"pendapatan-booking"}-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  // The page holds only the display-capped rows, so a bigger
                  // ask needs its own request; fall back to what we have if
                  // that fetch fails rather than losing the download.
                  const source =
                    (pageCount.source !== data ? pageCount.source : undefined) ??
                    (maxRows && maxRows > data.table.length
                      ? (await fetchAnalyticsRows<SalesServicesAnalytics>(
                          "sales",
                          { ...windowParams, view: "services" },
                          maxRows,
                        )) ?? data
                      : data);
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "pendapatan-booking",
                    studio: authState.business?.name ?? "",
                    data: source,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Row gutter={[16, 16]}>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: 16 }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))
          ) : (
            <>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<WalletOutlined />}
                  title={translate("analyticsPage.sales.kpi_total_revenue")}
                  information={translate("analyticsPage.columns.booking_revenue_info")}
                  value={formatCurrency(data?.total_revenue.value ?? 0)}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<CalendarOutlined />}
                  title={translate("analyticsPage.sales.kpi_sessions_completed")}
                  value={data?.sessions_completed.value ?? 0}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<BarChartOutlined />}
                  title={translate("analyticsPage.sales.kpi_revenue_per_session")}
                  value={formatCurrency(data?.revenue_per_session.value ?? 0)}
                  information={translate("analyticsPage.columns.booking_revenue_info")}
                />
              </Col>
            </>
          )}
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          {/* The one Laporan table whose row opens something other than a
              record page: the session detail modal. One line says so, where
              Pembelian keeps its figures note (chris, 2026-10-16). */}
          <div style={{ marginBottom: 14 }}>
            <span style={{ fontSize: textPx(13), color: colors.ink3 }}>
              {translate("analyticsPage.sales.session_row_hint")}
            </span>
          </div>
          <ReportTable<SalesServicesRow>
            serverTotal={{ total: data?.sessions_completed.value, noun: translate("pdf.noun.sesi") }}
            // The server's session key (schedule + occurrence): class_name +
            // start_time can collide across schedules, and duplicate keys make
            // React duplicate rows on every re-sort. The fallback only covers
            // an older server that doesn't send `session_key` yet.
            rowKey={(row) => row.session_key ?? `${row.class_name}-${row.start_time}`}
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsSalesServices"
            onSortChange={onSortChange}
            onRow={(row) => ({
              onClick: () => setSelectedSession(row),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
      <SessionDetailModal
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
      />
    </>
  );
};

export default SalesServicesPage;
